import type { SaveData } from '../types';

type Effect = 'arrival' | 'order' | 'ready' | 'serve' | 'coin' | 'clean' | 'success' | 'angry' | 'helper' | 'gorkem' | 'upgrade';

const notes: Record<Effect, [number, number, OscillatorType]> = {
  arrival: [523, 0.12, 'sine'],
  order: [659, 0.12, 'triangle'],
  ready: [880, 0.2, 'sine'],
  serve: [698, 0.16, 'triangle'],
  coin: [1047, 0.18, 'sine'],
  clean: [420, 0.1, 'triangle'],
  success: [784, 0.55, 'sine'],
  angry: [175, 0.28, 'sawtooth'],
  helper: [587, 0.16, 'triangle'],
  gorkem: [740, 0.26, 'sine'],
  upgrade: [988, 0.3, 'triangle'],
};

export class AudioSystem {
  private context?: AudioContext;
  private music?: HTMLAudioElement;
  private musicPlay?: Promise<void>;
  private interactionUnlocked = false;
  private musicFailed = false;
  private musicDuck = 1;
  private settings: SaveData['settings'];

  constructor(settings: SaveData['settings']) {
    this.settings = settings;
  }

  setSettings(settings: SaveData['settings']): void {
    this.settings = settings;
    this.applyMusicVolume();
    if (this.interactionUnlocked && this.musicVolume > 0) this.startMusic();
  }

  unlock(): void {
    this.interactionUnlocked = true;
    if (typeof AudioContext !== 'undefined') {
      this.context ??= new AudioContext();
      void this.context.resume().catch(() => undefined);
    }
    this.startMusic();
  }

  setMusicDuck(multiplier: number): void {
    this.musicDuck = Math.max(0, Math.min(1, multiplier));
    this.applyMusicVolume();
  }

  play(effect: Effect): void {
    if (this.settings.master <= 0 || this.settings.effects <= 0) return;
    if (!this.context) return;
    const [frequency, duration, type] = notes[effect];
    const oscillator = this.context.createOscillator();
    const gain = this.context.createGain();
    oscillator.frequency.value = frequency;
    oscillator.type = type;
    gain.gain.setValueAtTime(0.0001, this.context.currentTime);
    gain.gain.exponentialRampToValueAtTime(
      Math.max(0.0001, this.settings.master * this.settings.effects * 0.11),
      this.context.currentTime + 0.015,
    );
    gain.gain.exponentialRampToValueAtTime(0.0001, this.context.currentTime + duration);
    oscillator.connect(gain).connect(this.context.destination);
    oscillator.start();
    oscillator.stop(this.context.currentTime + duration + 0.02);
  }

  private get musicVolume(): number {
    return Math.max(0, Math.min(1, this.settings.master * this.settings.music * this.musicDuck * 0.58));
  }

  private ensureMusic(): HTMLAudioElement | undefined {
    if (this.music || this.musicFailed || typeof Audio === 'undefined') return this.music;
    const music = new Audio(`${import.meta.env.BASE_URL}assets/audio/little-corner-coffee-jazz.mp3`);
    music.dataset.littleCornerMusic = 'true';
    music.loop = true;
    music.preload = 'auto';
    music.setAttribute('playsinline', '');
    music.addEventListener('error', () => {
      this.musicFailed = true;
      this.musicPlay = undefined;
      console.warn('Little Corner background music could not be loaded; continuing without music.');
    }, { once: true });
    music.addEventListener('ended', () => {
      if (!this.interactionUnlocked || this.musicVolume <= 0 || this.musicFailed) return;
      music.currentTime = 0;
      void music.play().catch(() => undefined);
    });
    document.body.append(music);
    this.music = music;
    this.applyMusicVolume();
    return music;
  }

  private applyMusicVolume(): void {
    if (this.music) this.music.volume = this.musicVolume;
  }

  private startMusic(): void {
    if (!this.interactionUnlocked || this.musicFailed || this.musicVolume <= 0) return;
    const music = this.ensureMusic();
    if (!music || !music.paused || this.musicPlay) return;
    this.musicPlay = music.play()
      .catch(() => undefined)
      .finally(() => { this.musicPlay = undefined; });
  }
}
